import { ChangeDetectorRef, Component, OnInit, computed, inject, input, model, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { ButtonComponent } from '@libs/front/components/button/button.component';
import { AutocompleteComponent, TreeNodeAutoComplete } from '@libs/front/components/autocomplete/autocomplete.component';

import { ControlPoint } from 'src/app/bo/controlPoint';
import { SelectOption } from 'src/app/bo/select-option';
import { Site } from 'src/app/bo/site';
import { VibrationData } from 'src/app/bo/vibrationsForChart/vibration-data';
import { VibrationRecord } from 'src/app/bo/vibrationsForChart/vibration-record';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { Blast, HoleService } from 'src/app/services/hole.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { VibrationsRecordService } from 'src/app/services/vibrations-record.service';

/**
 * Refonte de `app-records-assignment` : même comportement (affectation / désaffectation
 * d'un point de mesure, calcul de la distance au tir, toasts), rendu avec les composants
 * génériques de `@libs/front/components`.
 */
@Component({
  selector: 'app-blast-records-assignment',
  templateUrl: './blast-records-assignment.component.html',
  styleUrls: ['./blast-records-assignment.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ButtonComponent, AutocompleteComponent],
})
export class BlastRecordsAssignmentComponent implements OnInit {
  readonly siteId = input<number>();
  readonly isControlPointAssigned = model<boolean>(false);
  readonly vibrationRecord = input<VibrationData>();
  readonly controlPoints = input<ControlPoint[]>([]);
  readonly deviceRef = input<string>();

  protected readonly assignedControlPoint = signal<ControlPoint | undefined>(undefined);
  protected readonly placeholderCp = 'VIBRATION.SELECT_CONTROL_POINT';

  /** Points de mesure proposés par l'autocomplete générique. */
  protected readonly controlPointNodes = computed<TreeNodeAutoComplete[]>(() =>
    this.controlPoints().map(cp => ({ id: cp.controlPointId, label: cp.name })),
  );

  /** Le record avait déjà un point de mesure : on propose d'annuler la modification. */
  protected readonly canCancel = computed(() => {
    const id = this.vibrationRecord()?.controlPointId;
    return id !== 0 && id !== null && id !== undefined;
  });

  private optionsControlPoints: SelectOption[] = [];
  private selectedOptionCp: SelectOption | undefined;
  private site!: Site;

  private readonly utilisateurService = inject(UtilisateurService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly fonctionsService = inject(FonctionsService);
  private readonly vibrationRecordService = inject(VibrationsRecordService);
  private readonly holeService = inject(HoleService);

  ngOnInit(): void {
    this.site = this.utilisateurService.currentSite();
    this.getControlPointsBySite(this.siteId()!);
  }

  protected modifyAssignedCp(): void {
    this.isControlPointAssigned.set(!this.isControlPointAssigned());
  }

  protected cancel(type: string): void {
    if (type === 'controlPoint') {
      this.isControlPointAssigned.set(true);
    }
  }

  private getControlPointsBySite(siteId: number): void {
    if (this.vibrationRecord()!.controlPointId) {
      this.assignedControlPoint.set(
        this.controlPoints().find(cp => cp.controlPointId === this.vibrationRecord()!.controlPointId),
      );
      this.cdRef.detectChanges();
    }

    this.optionsControlPoints = this.controlPoints().map(cp => ({
      id: cp.controlPointId,
      name: cp.name,
    }));
  }

  /** Traduit la sélection de l'autocomplete générique en `SelectOption`. */
  protected onCpNodeSelected(node: TreeNodeAutoComplete): void {
    const option = this.optionsControlPoints.find(o => o.id === node?.id);
    this.onOptionCpSelected(option ?? { id: node?.id, name: node?.label });
  }

  protected onOptionCpSelected(option: SelectOption): void {
    this.selectedOptionCp = option;
  }

  protected assignRecord(type: string): void {
    const isControlPoint = type === 'controlPoint';
    const errorMessage = 'Error assigning measurement point';

    if (isControlPoint && !this.selectedOptionCp) {
      this.fonctionsService.launch_toast(false, false, 'Please select a measurement point to assign');
      return;
    }

    if (isControlPoint) {
      this.vibrationRecord()!.controlPointId = this.selectedOptionCp!.id;
    }

    const submitUpdate = () => {
      const data: VibrationRecord = {
        information: {
          currentURL: window.location.href,
          regionName: this.site.region?.nom,
          siteName: this.site.nom,
          controlPointName: this.selectedOptionCp?.name,
        },
        vibrationRecord: this.vibrationRecord()!,
      };
      this.vibrationRecordService.updateRecord(this.siteId()!, this.vibrationRecord()!.id.toString(), data).subscribe({
        next: () => {
          if (isControlPoint) {
            this.assignedControlPoint.set(
              this.controlPoints().find(cp => cp.controlPointId === this.selectedOptionCp!.id),
            );
          }
          this.isControlPointAssigned.set(true);
          this.fonctionsService.launch_toast(true);
          this.cdRef.detectChanges();
        },
        error: () => {
          this.fonctionsService.launch_toast(false, false, errorMessage);
        },
      });
    };

    if (isControlPoint) {
      const controlPoint = this.controlPoints().find(cp => cp.controlPointId === this.selectedOptionCp!.id);
      const blastId = this.vibrationRecord()!.blastId;
      if (controlPoint && blastId) {
        this.holeService.GetBlastHoles(blastId).then((blast: Blast) => {
          const distance = this.holeService.computeMinDistanceToHoles(controlPoint, blast?.Holes ?? []);
          if (distance != null) {
            this.vibrationRecord()!.distance = distance;
          }
          submitUpdate();
        }).catch(() => {
          submitUpdate();
        });
        return;
      }
    }

    submitUpdate();
  }

  protected unassignRecord(type: string): void {
    const isControlPoint = type === 'controlPoint';
    const errorMessage = 'Error unassigning control point';

    if (isControlPoint) {
      this.vibrationRecord()!.controlPointId = 0;
      this.vibrationRecord()!.distance = undefined;
    }

    const data: VibrationRecord = {
      information: {
        currentURL: window.location.href,
        regionName: this.site.region?.nom,
        siteName: this.site.nom,
        controlPointName: '',
      },
      vibrationRecord: this.vibrationRecord()!,
    };

    this.vibrationRecordService.updateRecord(this.siteId()!, this.vibrationRecord()!.id.toString(), data).subscribe({
      next: () => {
        this.fonctionsService.launch_toast(true);
      },
      error: (err: any) => {
        console.error(errorMessage, err);
      },
    });
  }
}
